import { GeoCoverage, MinFitLevel, UserSettings } from '../types';

export interface PreferenceOption<T> {
  value: T;
  /** Radio label on the Settings page, including the recommendation hint. */
  label: string;
  /** Compact label for the widen strip's `current → next` track. */
  shortLabel: string;
}

// Display order for the Settings radios — unchanged from when these lived in
// SearchPreferencesSection. Widening order is defined separately below,
// because for match level the two run in opposite directions.
//
// No distance figures: `distance` is a best-effort crawl parameter (design §12)
// that scopes what enters a shared job pool. Nothing filters by radius at read
// time, and a second user searching the same role+location inherits the wider
// pool — so a "~15 km" promise is one the system cannot keep.
export const GEO_OPTIONS: PreferenceOption<GeoCoverage>[] = [
  {
    value: 'pinpoint',
    label: 'Exact city — when the job board supports it',
    shortLabel: 'Exact city',
  },
  { value: 'nearby', label: 'Nearby (recommended)', shortLabel: 'Nearby' },
  { value: 'broad', label: 'Wider area', shortLabel: 'Wider area' },
];

export const FIT_OPTIONS: PreferenceOption<MinFitLevel>[] = [
  {
    value: 'potential_candidate',
    label: 'Potential Candidate and up (recommended)',
    shortLabel: 'Potential and up',
  },
  {
    value: 'strong_alignment',
    label: 'Strong Alignment and up',
    shortLabel: 'Strong and up',
  },
  { value: 'perfect_fit', label: 'Perfect Fit only', shortLabel: 'Perfect fit only' },
];

// Narrowest → widest. `broad` surfaces the most roles; `potential_candidate` is
// the loosest match floor, so it sits at the wide end even though the Settings
// radios list it first.
const GEO_BY_WIDTH: GeoCoverage[] = ['pinpoint', 'nearby', 'broad'];
const FIT_BY_WIDTH: MinFitLevel[] = ['perfect_fit', 'strong_alignment', 'potential_candidate'];

export type WidenableField = 'geoCoverage' | 'minFitLevel';

export interface WidenLever {
  field: WidenableField;
  /** Row label in the strip, matching the Settings fieldset legend. */
  legend: string;
  currentLabel: string;
  /** The next step out, or null when this lever is already at its widest. */
  next: GeoCoverage | MinFitLevel | null;
  nextLabel: string | null;
}

const shortLabelOf = <T,>(options: PreferenceOption<T>[], value: T): string =>
  options.find((option) => option.value === value)?.shortLabel ?? String(value);

const stepOut = <T,>(byWidth: T[], current: T): T | null => {
  const index = byWidth.indexOf(current);
  // An unknown value (a level added by the backend the client doesn't ship yet)
  // yields -1: treat it as "no room" rather than guessing a direction.
  if (index < 0 || index === byWidth.length - 1) return null;
  return byWidth[index + 1];
};

/**
 * Both search-preference levers with the single step out available on each, or
 * null where the user is already at the widest setting.
 *
 * A lever with no room must never be offered as an action: with the shipped
 * defaults (`nearby` + `potential_candidate`) the match level is ALREADY the
 * loosest, so "lower your minimum match level" would be advice the product
 * cannot honour.
 */
const buildLever = <T,>(
  field: WidenableField,
  legend: string,
  options: PreferenceOption<T>[],
  byWidth: T[],
  current: T
): WidenLever => {
  const next = stepOut(byWidth, current);
  return {
    field,
    legend,
    currentLabel: shortLabelOf(options, current),
    next: next as GeoCoverage | MinFitLevel | null,
    nextLabel: next === null ? null : shortLabelOf(options, next),
  };
};

export const nextWiderStep = (settings: UserSettings): WidenLever[] => [
  buildLever('geoCoverage', 'Search area', GEO_OPTIONS, GEO_BY_WIDTH, settings.geoCoverage),
  buildLever('minFitLevel', 'Match level', FIT_OPTIONS, FIT_BY_WIDTH, settings.minFitLevel),
];

/** True when at least one lever can still be widened — the strip's gate. */
export const hasRoomToWiden = (settings: UserSettings): boolean =>
  nextWiderStep(settings).some((lever) => lever.next !== null);
